import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useStacklineTheme, StacklineTheme } from '@stackline/ui';
import ReactDOMServer from 'react-dom/server';
import _merge from 'lodash/merge';
import HoverTooltip from '../HoverTooltip/HoverTooltip';
import { useMetricFormatter, useAppSelector, useWeekRange, useComparisonPeriod, WeekRange, ComparisonPeriod, useQueryParamValue } from 'src/utils/Hooks';
import { BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { getWeekIdRange } from 'src/utils/dateUtils';
import { ValueOf } from 'sl-api-connector';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange } from 'src/utils/app';
import { useSplineChartV2Data } from './useSplineChartV2Data';
import { formatIsoDateToMMDD, SplineChartV2DataPointWithMeta } from './splineChartV2Utils';

/**
 * Metadata for a single week
 */
export interface WeekMeta {
  year: number;
  week: number;
  weekLabel: string;
  ts: number; // timestamp (ms)
  isoDate: string; // YYYY-MM-DD
}

/**
 * A single data point for the spline chart.
 * This is the simplified format that consumers will provide.
 */
export interface SplineChartV2DataPoint {
  /** Week ID in format YYYYWW (e.g., 202501) */
  weekId: number;
  /** The Y value for this data point */
  value: number | null;
  /** Optional custom name for the point (defaults to week number) */
  name?: string;
}

/**
 * Series configuration for the chart
 */
export interface SplineChartV2Series {
  /** Unique identifier for the series */
  id: string;
  /** Display name for the series */
  name: string;
  /** Data points for this series */
  data: SplineChartV2DataPoint[];
  /** Custom color for the series (optional, will use theme defaults) */
  color?: string;
  /** Whether this series should be rendered as dashed (e.g., for forecasts) */
  isDashed?: boolean;
  /** Z-index for layering (higher = on top) */
  zIndex?: number;
}

export interface SplineChartV2Props {
  /** Primary series data */
  primarySeries: SplineChartV2Series;
  /** Optional comparison/secondary series */
  comparisonSeries?: SplineChartV2Series;
  /** Optional forecast series (rendered as dashed line) */
  forecastSeries?: SplineChartV2Series;
  /** Metric type for formatting values */
  metricType: ValueOf<typeof METRICTYPE>;
  /** Preview mode dimensions */
  preview?: 'large' | 'small' | { width: number; height: number };
  /** Chart width */
  width?: number | string;
  /** Remove spacing around the chart */
  removeSpacing?: boolean;
  /** Placeholder to show when there's no data */
  NoDataPlaceholder?: React.ReactNode;
  /** Optional Highcharts options override */
  chartOptionsOverride?: Partial<Highcharts.Options>;
  /**
   * Custom tooltip renderer. If not provided, uses the default HoverTooltip.
   */
  renderCustomTooltip?: (params: {
    primaryPoint: SplineChartV2DataPointWithMeta;
    comparisonPoint?: SplineChartV2DataPointWithMeta;
    percentChange?: number;
    theme: StacklineTheme;
    formatMetric: ReturnType<typeof useMetricFormatter>;
    metricType: ValueOf<typeof METRICTYPE>;
  }) => string;
  /**
   * Whether to show markers on the last data point
   */
  showLastPointMarker?: boolean;
  /**
   * Optional override for category start week ID.
   * If provided with categoryEndWeekId, the chart will use these to generate categories
   * instead of deriving from Redux mainTimePeriod.
   * This is useful for YTD + Prior Year where categories should span the full comparison year.
   */
  categoryStartWeekId?: number;
  /**
   * Optional override for category end week ID.
   * Must be provided along with categoryStartWeekId.
   */
  categoryEndWeekId?: number;
  /**
   * Optional override for prior period detection.
   * When true, merges comparison and primary into one continuous line.
   * If undefined, detection is based on Redux comparisonTimePeriod.id.
   */
  isPriorPeriodOverride?: boolean;
}

/**
 * SplineChartV2 - A simplified spline chart that uses categories-based xAxis
 *
 * Key differences from SplineChart:
 * - Uses categories (week labels) instead of timestamp-based x positioning
 * - Simpler data format: just { weekId, value } objects
 * - Dynamically generates categories from the time period
 * - Less data transformation required from API responses
 */
const SplineChartV2 = ({
  primarySeries,
  comparisonSeries,
  forecastSeries,
  metricType,
  preview,
  width,
  removeSpacing = false,
  NoDataPlaceholder,
  chartOptionsOverride,
  renderCustomTooltip,
  showLastPointMarker = false,
  categoryStartWeekId,
  categoryEndWeekId,
  isPriorPeriodOverride
}: SplineChartV2Props) => {
  const theme = useStacklineTheme();
  const formatMetric = useMetricFormatter();

  // Get time period info for internal marker logic (like V1)
  const weekRange = useWeekRange();
  const comparisonPeriod = useComparisonPeriod();
  const tab = useQueryParamValue('tab');

  // Internal logic to determine if we should show last point marker (matches V1 behavior)
  const isLastWeekWithPriorYear = useMemo(() => {
    return (
      tab !== 'forecasts' &&
      tab !== BEACON_TABS.ADVERTISING &&
      weekRange === WeekRange.LAST_WEEK &&
      comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
    );
  }, [comparisonPeriod, weekRange, tab]);

  // Combine prop with internal logic
  const shouldShowLastPointMarker = showLastPointMarker || isLastWeekWithPriorYear;

  // Use the data transformation hook
  const {
    isPriorPeriod,
    mergedData,
    categoryCount,
    xAxisCategories,
    primaryDataMapped,
    comparisonDataMapped,
    forecastDataMapped,
    comparisonLength
  } = useSplineChartV2Data({
    primarySeries,
    comparisonSeries,
    forecastSeries,
    categoryStartWeekId,
    categoryEndWeekId,
    isPriorPeriodOverride
  });

  // Chart dimensions
  const chartDimensionsMap = useMemo(
    () => ({
      large: { width: width || 299, height: 85 },
      small: { width: width || 178, height: 85 },
      custom: typeof preview === 'object' ? preview : undefined
    }),
    [width, preview]
  );

  // Calculate min value for yAxis
  const chartMin = useMemo(() => {
    const allYValues = [
      ...primaryDataMapped.map((p) => p?.value ?? 0),
      ...comparisonDataMapped.map((p) => p?.value ?? 0),
      ...forecastDataMapped.map((p) => p?.value ?? 0)
    ].filter((v) => v !== null);
    return allYValues.length > 0 ? Math.min(...allYValues) : 0;
  }, [primaryDataMapped, comparisonDataMapped, forecastDataMapped]);

  const textStyles: Highcharts.CSSObject = useMemo(
    () => ({
      fontFamily: theme.text.fontFamily,
      color: theme.colors.primary,
      fontSize: '12px'
    }),
    [theme.text.fontFamily, theme.colors.primary]
  );

  const seriesMarker = useMemo(
    () => ({
      enabled: false,
      symbol: 'circle' as const,
      states: {
        hover: {
          radius: 4,
          lineWidth: 0
        }
      }
    }),
    []
  );

  // Only use pointPlacement: 'on' when there's more than 1 data point
  // With just 1 point, pointPlacement can cause issues
  const shouldUsePointPlacement = categoryCount > 1;

  const previewOptions: Partial<Highcharts.Options> = {
    chart: {
      ...(chartDimensionsMap[typeof preview === 'object' ? 'custom' : preview] || {}),
      marginBottom: 10,
      backgroundColor: 'transparent'
    },
    xAxis: {
      visible: false,
      height: 0,
      minPadding: 0,
      maxPadding: 0
    },
    yAxis: [{ visible: false }],
    plotOptions: {
      series: {
        lineWidth: 1.5,
        marker: {
          enabled: true 
        },
        ...(shouldUsePointPlacement ? { pointPlacement: 'on' } : {})
      }
    }
  };
  // Build series configuration

  const series: Highcharts.SeriesOptionsType[] = useMemo(() => {
    const result: Highcharts.SeriesOptionsType[] = [];

    // Forecast series (dashed)
    if (forecastSeries && forecastDataMapped.length > 0) {
      result.push({
        type: 'spline',
        name: forecastSeries.name,
        data: forecastDataMapped.map((point) => ({
          name: point?.weekId ? String(point.weekId) : '',
          y: point?.value ?? null,
          weekId: point?.weekId ?? 0,
          meta: point?.meta
        })),
        dashStyle: 'Dash',
        color: forecastSeries.color || theme.colors.primary,
        marker: seriesMarker,
        enableMouseTracking: true,
        zIndex: forecastSeries.zIndex ?? 1
      } as Highcharts.SeriesSplineOptions);
    }

    // Merged mode: single continuous line (comparison → primary) with color zones
    if (isPriorPeriod && mergedData && comparisonSeries) {
      result.push({
        type: 'spline',
        name: primarySeries.name,
        data: mergedData.map((point) => ({
          name: point.weekId ? String(point.weekId) : '',
          y: point.value ?? null,
          weekId: point.weekId ?? 0,
          meta: point.meta
        })),
        color: primarySeries.color || theme.colors.primary,
        marker: seriesMarker,
        zIndex: primarySeries.zIndex ?? 2,
        // Use zones to color comparison portion with secondary color
        zoneAxis: 'x',
        zones: [
          {
            // Comparison portion (indices 0 to comparisonLength - 1)
            value: comparisonLength,
            color: comparisonSeries.color || theme.colors.secondary
          },
          {
            // Primary portion (indices comparisonLength onwards)
            color: primarySeries.color || theme.colors.primary
          }
        ]
      } as Highcharts.SeriesSplineOptions);
    } else {
      // Standard mode: separate primary and comparison series

      // Primary series
      // Primary series - no point-level markers, using renderer instead
      result.push({
        type: 'spline',
        name: primarySeries.name,
        data: primaryDataMapped.map((point) => ({
          name: point?.weekId ? String(point.weekId) : '',
          y: point?.value ?? null,
          weekId: point?.weekId ?? 0,
          meta: point?.meta
        })),
        color: primarySeries.color || theme.colors.primary,
        marker: seriesMarker,
        zIndex: primarySeries.zIndex ?? 2
      } as Highcharts.SeriesSplineOptions);

      // Comparison series - no markers
      if (comparisonSeries && comparisonDataMapped.length > 0) {
        result.push({
          type: 'spline',
          name: comparisonSeries.name,
          data: comparisonDataMapped.map((point) => ({
            name: point?.weekId ? String(point.weekId) : '',
            y: point?.value ?? null,
            weekId: point?.weekId ?? 0,
            meta: point?.meta
          })),
          color: comparisonSeries.color || theme.colors.secondary,
          marker: seriesMarker,
          zIndex: comparisonSeries.zIndex ?? 0
        } as Highcharts.SeriesSplineOptions);
      }
    }

    return result;
  }, [
    forecastSeries,
    forecastDataMapped,
    isPriorPeriod,
    mergedData,
    primarySeries,
    primaryDataMapped,
    comparisonSeries,
    comparisonDataMapped,
    comparisonLength,
    theme.colors.primary,
    theme.colors.secondary,
    seriesMarker
  ]);

  // Primary series color for the marker
  const primaryColor = primarySeries?.color || theme.colors.primary;

  const options: Highcharts.Options = _merge(
    {
      chart: {
        type: 'spline',
        ...(removeSpacing ? { spacing: [0, 0, 0, 0] } : {}),
        spacingLeft: 0,
        events: {
          render(this: Highcharts.Chart) {
            // Only show marker for Last Week + Prior Year
            if (!shouldShowLastPointMarker) {
              // Remove existing marker if we shouldn't show it
              if ((this as any).customMarker) {
                (this as any).customMarker.destroy();
                (this as any).customMarker = null;
              }
              return;
            }

            // Get the primary series by name (not comparison)
            const allSplineSeries = this.series.filter(s => s.type === 'spline' && s.visible);
            // Find the primary series - it should have the primary series name and highest zIndex
            const primarySeriesObj = allSplineSeries.find(s => (s.options as any).zIndex >= 2) || allSplineSeries[0];
            if (!primarySeriesObj || !primarySeriesObj.points.length) return;

            const points = primarySeriesObj.points;
            // Find the last point that has actual data (not null)
            let lastPoint: (Highcharts.Point & { plotX?: number; plotY?: number }) | null = null;
            for (let i = points.length - 1; i >= 0; i--) {
              if (points[i].y !== null && points[i].y !== undefined) {
                lastPoint = points[i] as Highcharts.Point & { plotX?: number; plotY?: number };
                break;
              }
            }
            if (!lastPoint) return;

            const x = this.plotLeft + (lastPoint.plotX ?? 0);
            const y = this.plotTop + (lastPoint.plotY ?? 0);

            if (!(this as any).customMarker) {
              // First render - create the marker
              (this as any).customMarker = this.renderer
                .circle(x, y, 5)
                .attr({ fill: primaryColor, zIndex: 5 })
                .add();
            } else {
              // Redraw - move the marker
              (this as any).customMarker.attr({ cx: x, cy: y });
            }
          }
        }
      },
      title: { text: '' },
      exporting: { enabled: false },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: {
        enabled: true,
        shared: true,
        shadow: false,
        hideDelay: 0,
        outside: true,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        useHTML: true,
        formatter() {
          // For merged prior period mode, handle single series with zones
          if (isPriorPeriod && comparisonSeries) {
            const point = this.points?.[0];
            if (!point) return '';

            const pointIndex = point.point.index;
            const isComparisonPortion = pointIndex < comparisonLength;

            const meta = (point.point as any).meta as WeekMeta;
            const value = point.y ?? null;

            // Determine the correct color based on which portion we're in
            const tooltipColor = isComparisonPortion
              ? (comparisonSeries.color || theme.colors.secondary)
              : (primarySeries.color || theme.colors.primary);

            return ReactDOMServer.renderToStaticMarkup(
              <HoverTooltip
                theme={theme}
                title={`Week ${meta?.weekLabel ?? ''}`}
                primaryColor={tooltipColor}
                primaryMetric={
                  value !== null
                    ? formatMetric(value, metricType, {
                        decimalPlaces: 2,
                        showFullValue: metricType === METRICTYPE.PERCENT,
                        showNegative: true
                      })
                    : undefined
                }
                primaryMetricLabel={meta?.isoDate ? formatIsoDateToMMDD(meta.isoDate) : undefined}
              />
            );
          }

          // Standard mode: find primary, comparison, and forecast points
          const primaryPoint = this.points?.find(
            (p) => p.series.name === primarySeries.name
          ) as
            | (Highcharts.TooltipFormatterContextObject & {
                point: { weekId: number; meta: WeekMeta };
              })
            | undefined;

          const comparisonPoint = comparisonSeries
            ? (this.points?.find(
                (p) => p.series.name === comparisonSeries.name
              ) as
                | (Highcharts.TooltipFormatterContextObject & {
                    point: { weekId: number; meta: WeekMeta };
                  })
                | undefined)
            : undefined;

          const forecastPoint = forecastSeries
            ? (this.points?.find(
                (p) => p.series.name === forecastSeries.name
              ) as
                | (Highcharts.TooltipFormatterContextObject & {
                    point: { weekId: number; meta: WeekMeta };
                  })
                | undefined)
            : undefined;

          // Need at least one point to show tooltip
          if (!primaryPoint && !comparisonPoint && !forecastPoint) return '';

          // For forecasts: if no primary but have forecast, use forecast as the "primary" display
          // This handles the case where primaryData is empty but forecastData exists
          const effectivePrimaryPoint = primaryPoint || forecastPoint;

          // Get weekId and meta from whichever point is available
          const activePoint = effectivePrimaryPoint || comparisonPoint;
          const meta = (activePoint!.point as any).meta as WeekMeta;

          const primaryDataPoint: SplineChartV2DataPointWithMeta | undefined = effectivePrimaryPoint
            ? {
                weekId: (effectivePrimaryPoint.point as any).weekId,
                value: effectivePrimaryPoint.y ?? null,
                meta: (effectivePrimaryPoint.point as any).meta as WeekMeta
              }
            : undefined;

          const comparisonDataPoint: SplineChartV2DataPointWithMeta | undefined = comparisonPoint
            ? {
                weekId: (comparisonPoint.point as any).weekId,
                value: comparisonPoint.y ?? null,
                meta: (comparisonPoint.point as any).meta as WeekMeta
              }
            : undefined;

          const percentChange =
            comparisonDataPoint &&
            primaryDataPoint &&
            comparisonDataPoint.value !== null &&
            primaryDataPoint.value !== null
              ? calculatePercentChange(primaryDataPoint.value, comparisonDataPoint.value)
              : undefined;

          // Use custom renderer if provided
          if (renderCustomTooltip && primaryDataPoint) {
            return renderCustomTooltip({
              primaryPoint: primaryDataPoint,
              comparisonPoint: comparisonDataPoint,
              percentChange,
              theme,
              formatMetric,
              metricType
            });
          }

          // Determine primary color - use forecast color if showing forecast point, otherwise primary color
          const primaryTooltipColor = effectivePrimaryPoint
            ? ((effectivePrimaryPoint.color as string) || theme.colors.primary)
            : theme.colors.primary;

          // Default tooltip - show whatever data is available
          return ReactDOMServer.renderToStaticMarkup(
            <HoverTooltip
              theme={theme}
              title={`Week ${meta.weekLabel}`}
              primaryColor={primaryTooltipColor}
              primaryMetric={
                primaryDataPoint
                  ? formatMetric(primaryDataPoint.value, metricType, {
                      decimalPlaces: 2,
                      showFullValue: metricType === METRICTYPE.PERCENT,
                      showNegative: true
                    })
                  : undefined
              }
              primaryMetricLabel={primaryDataPoint?.meta.isoDate ? formatIsoDateToMMDD(primaryDataPoint.meta.isoDate) : undefined}
              change={
                percentChange !== undefined
                  ? formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })
                  : undefined
              }
              rawChange={percentChange}
              secondaryMetric={
                comparisonDataPoint
                  ? formatMetric(comparisonDataPoint.value, metricType, {
                      decimalPlaces: 2,
                      showFullValue: metricType === METRICTYPE.PERCENT,
                      showNegative: true
                    })
                  : undefined
              }
              secondaryMetricLabel={comparisonDataPoint?.meta.isoDate ? formatIsoDateToMMDD(comparisonDataPoint.meta.isoDate) : undefined}
              secondaryColor={comparisonPoint ? (comparisonPoint.color as string) : theme.colors.secondary}
            />
          );
        }
      },
      xAxis: {
        type: 'category',
        tickLength: 0,
        lineColor: 'transparent',
        categories: xAxisCategories,
        labels: {
          style: textStyles,
          rotation: 0,
          enabled: true,
          // Ensure labels show even with many categories
          step: Math.max(1, Math.floor(xAxisCategories.length / 12)) // Show ~12 labels max
        },
        visible: true,
        title: { text: '' },
        // Ensure axis line is visible (for debugging)
        lineWidth: 1,
        // Ensure points span full width (especially important for 2-point charts)
        minPadding: 0,
        maxPadding: 0
      },
      yAxis: [
        {
          gridLineWidth: 0,
          title: { text: '' },
          labels: {
            formatter() {
              return formatMetric(Number(this.value), metricType, {
                decimalPlaces: 2,
                showFullValue: false,
                showNegative: true
              });
            },
            style: textStyles
          },
          min: Math.min(0, chartMin)
        }
      ],
      plotOptions: {
        series: {
          ...(shouldUsePointPlacement ? { pointPlacement: 'on' } : {})
        }
      },
      series
    } as Highcharts.Options,
    preview ? previewOptions : {},
    chartOptionsOverride || {}
  );

  const noSeriesData = series.every((s) => {
    const seriesData = (s as Highcharts.SeriesSplineOptions).data;
    return !seriesData || (seriesData as any[]).every((d) => d.y === null);
  });

  return noSeriesData && NoDataPlaceholder ? (
    <>{NoDataPlaceholder}</>
  ) : (
    <HighchartsReact highcharts={Highcharts} options={options} />
  );
};

export default SplineChartV2;

/**
 * Utility hook to generate week IDs for the current time period
 */
export const useWeekIdsForTimePeriod = () => {
  const startWeekId = useAppSelector((state) => state.mainTimePeriod.startWeek);
  const endWeekId = useAppSelector((state) => state.mainTimePeriod.endWeek);

  return useMemo(() => {
    if (!startWeekId || !endWeekId) return [];
    return getWeekIdRange(startWeekId, endWeekId);
  }, [startWeekId, endWeekId]);
};

